/**
 * The F4 "Add column" brief: an extend-only rewrite of one dataset's SQL,
 * distinct from B2's whole-card tier-eval prompt (`prompts.ts`'s
 * `cardPrompt`/`viewChangePrompt`, which choose between view/field/extend/
 * dataset for a new card). This one is triggered directly from the panel's
 * "+ Add column" row, always means extend, and never proposes a card.
 */
export function extendColumnPrompt(
  dataset: { name: string; sql: string; columns: Array<string> },
  request: string,
): string {
  return `Rewrite this LE Lens dataset's query to also return what is asked for, without changing any existing column.

Dataset: ${dataset.name}
Columns it returns today, exactly as spelled: ${dataset.columns.join(', ')}

Existing SQL:
\`\`\`sql
${dataset.sql}
\`\`\`

Requested addition: ${request}

Return exactly one fenced sql block containing the whole rewritten query. It must return every column listed above, spelled the same way and in a compatible type, plus whatever the request needs. Keep the dashboard's date binding (@start_date/@end_date) if the existing query has one. Do not drop a column to make room for the new one; if the request cannot be answered without dropping or changing an existing column, write the best rewrite you can and let the guard that runs after this decide, rather than silently leaving something out.

After the fenced block, on its own line, state the columns that make one row of the rewritten query unique:

  -- grain: <column, column, ...>

State the grain even when it is unchanged from today's dataset: it is checked against the rewritten query's own rows, not assumed.

If the request needs a table this dataset does not read, or data no rewrite of this query could produce, do not invent a join or a second query. Reply with exactly one line instead, naming what is missing:

  -- cannot: <what this dataset cannot be made to return>`;
}

const GRAIN_LINE = /^\s*--\s*grain:\s*(.+?)\s*$/im;
const CANNOT_LINE = /^\s*--\s*cannot:\s*(.+?)\s*$/im;

/** The `-- grain: a, b, c` line an extend answer carries, comma-split and trimmed. Undefined
 *  when the model left it out, so the caller can fall back to the dataset's current grain. */
export function extractGrainLine(answer: string): Array<string> | undefined {
  const match = answer.match(GRAIN_LINE);
  if (!match) {
    return undefined;
  }
  const keys = match[1]
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
  return keys.length ? keys : undefined;
}

/** The `-- cannot: <reason>` refusal line, when the model answered with one instead of SQL. */
export function extractCannotLine(answer: string): string | undefined {
  return answer.match(CANNOT_LINE)?.[1]?.trim();
}
